import { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs';

import Picker from '../../../shared/uiComponents/DateAndTime';
import { CustomAlert, CustomButton, CustomCheckBox, CustomInput } from '../../../shared/uiComponents';
import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	addAnnotation,
	addBehaviorAnnotation,
	addGlobalAnnotation,
	getBehaviorReductionAllAnnotations,
	getTargetAllAnnotations,
} from '../../../redux/State/clientSlice/graphAnnotationSlice';

type AnnotationType = 'target' | 'behavior';

interface AddAnnotationPopupProperties {
	clientId: string;
	id: string;
	type: AnnotationType;
}

interface AddAnnotationContentProperties {
	clientId: string;
	id: string;
	onClose: () => void;
	type: AnnotationType;
}

const AddAnnotationContent = ({ clientId, id, type, onClose }: AddAnnotationContentProperties) => {
	const dispatch = useDispatch<DispatchProperties>();
	const today = new Date();
	const todayDayjs = dayjs(today);
	const isLoading = useSelector((state) => state.graphAnnotation.loading);

	const [currentDate, setCurrentDate] = useState<Dayjs>(todayDayjs);
	const [instructions, setInstructions] = useState<string>('');
	const [isGlobalAnnotation, setIsGlobalAnnotation] = useState<boolean>(false);

	const onInstructionsChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setInstructions(value);
	};

	const handleIsGlobalAnnotationChange = () => {
		setIsGlobalAnnotation((prev) => !prev);
	};

	const handleDateChange = (date: Dayjs | null) => {
		if (!date) return;
		setCurrentDate(date);
	};

	const handleCreateAnnotation = () => {
		if (instructions.length < 4 || !currentDate) return;
		const date = dateNormalizer(currentDate);
		const newData = {
			note: instructions,
			date,
		};

		if (isGlobalAnnotation) {
			dispatch(addGlobalAnnotation({ ...newData, clientId }))
				.then(unwrapResult)
				.then(() => {
					if (type === 'target') {
						dispatch(getTargetAllAnnotations(id));
					} else {
						dispatch(getBehaviorReductionAllAnnotations(id));
					}
				})
				.catch(errorNormalizer)
				.finally(() => onClose());
		} else if (type === 'target') {
			dispatch(addAnnotation({ ...newData, targetId: id }))
				.then(unwrapResult)
				.then(() => {
					dispatch(getTargetAllAnnotations(id));
				})
				.catch(errorNormalizer)
				.finally(() => onClose());
		} else {
			dispatch(addBehaviorAnnotation({ ...newData, behaviorId: id }))
				.then(unwrapResult)
				.then(() => {
					dispatch(getBehaviorReductionAllAnnotations(id));
				})
				.catch(errorNormalizer)
				.finally(() => onClose());
		}
	};

	return (
		<>
			<CustomInput
				label="Description"
				value={instructions}
				onChange={onInstructionsChange}
				multiline
				className="marginBottom16"
				error={instructions.length < 4}
				maxLength={250}
				errorMessage={instructions.length < 4 ? 'Please add description' : ''}
			/>
			<CustomCheckBox
				item={{
					checked: isGlobalAnnotation,
					id: 'isGlobalAnnotation',
					label: 'Apply to all targets?',
				}}
				onChange={handleIsGlobalAnnotationChange}
			/>
			<Picker.CustomDate label="Date" value={currentDate} onChange={handleDateChange} className="marginBottom16" />
			<div className="graphAnnotationsContainer">
				<CustomButton
					title="Save"
					onClick={handleCreateAnnotation}
					disabled={instructions.length < 4 || !currentDate}
					loading={isLoading}
				/>
				<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} />
			</div>
		</>
	);
};

const AddAnnotation = ({ clientId, id, type }: AddAnnotationPopupProperties) => {
	const [createAnnotation, setCreateAnnotation] = useState<boolean>(false);

	return (
		<>
			<CustomButton title="Add Annotation" onClick={() => setCreateAnnotation(true)} />
			<CustomAlert
				title="Add Annotation"
				onClose={() => setCreateAnnotation(false)}
				open={createAnnotation}
				Content={({ onClose }) => <AddAnnotationContent id={id} type={type} onClose={onClose} clientId={clientId} />}
				contentHeight="400px"
			/>
		</>
	);
};

export default AddAnnotation;
